import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, Image as ImageIcon, Upload, X } from 'lucide-react';
import axios from 'axios';
import API_CONFIG from '../../config/api';
import { useTranslation } from '../../i18n/LocaleContext';

const AdminOrders = () => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [editingOrder, setEditingOrder] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    price: '',
    status: 'active'
  });

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const response = await axios.get(API_CONFIG.getApiUrl('/jobs'), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data.jobs || []);
    } catch (error) {
      console.error('Fetch orders error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();

      formDataToSend.append('title', formData.title);
      formDataToSend.append('price', formData.price);
      formDataToSend.append('status', formData.status);

      if (imageFile) {
        formDataToSend.append('image', imageFile);
      }

      if (editingOrder) {
        // Update
        await axios.put(API_CONFIG.getApiUrl(`/jobs/${editingOrder.id}`), formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        alert(t('aOrdsUpdateSuccess'));
      } else {
        // Create
        await axios.post(API_CONFIG.getApiUrl('/jobs'), formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        });
        alert(t('aOrdsCreateSuccess'));
      }

      setShowModal(false);
      resetForm();
      fetchOrders();
    } catch (error) {
      console.error('Submit error:', error);
      alert(error.response?.data?.error || t('aOrdsErrorOccurred'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (order) => {
    setEditingOrder(order);
    setFormData({
      title: order.product_name,
      price: order.price,
      status: order.status
    });
    setImagePreview(API_CONFIG.getImageUrl(order.product_image));
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('aOrdsDeleteConfirm'))) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(API_CONFIG.getApiUrl(`/jobs/${id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert(t('aOrdsDeleteSuccess'));
      fetchOrders();
    } catch (error) {
      console.error('Delete error:', error);
      alert(t('aOrdsDeleteFailed'));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      price: '',
      status: 'active'
    });
    setImageFile(null);
    setImagePreview(null);
    setEditingOrder(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  return (
    <div>
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-white">{t('aOrdsTitle')}</h1>
          <p className="text-gray-400 mt-1">{t('aOrdsSubtitle')}</p>
        </div>
        <button
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center gap-2 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-500 font-medium"
        >
          <Plus className="w-5 h-5" />
          {t('aOrdsAddOrder')}
        </button>
      </div>

      {/* Pool đơn hàng */}
      <div className="mb-6">
        <h2 className="text-xl font-bold text-white">{t('aOrdsPoolTitle')}</h2>
        <p className="text-gray-400 text-sm mt-1">{t('aOrdsPoolSubtitle')}</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">{t('aOrdsTotalOrders')}</p>
          <p className="text-2xl font-bold text-white">{orders.length}</p>
        </div>
        <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">{t('aOrdsActive')}</p>
          <p className="text-2xl font-bold text-white">{orders.filter(o => o.status === 'active').length}</p>
        </div>
        <div className="bg-gray-800 rounded-xl shadow-sm p-4 border border-gray-700">
          <p className="text-gray-400 text-sm">{t('aOrdsTotalCommission')}</p>
          <p className="text-2xl font-bold text-white">
            {formatCurrency(orders.reduce((sum, o) => sum + parseFloat(o.commission || 0), 0))}
          </p>
        </div>
      </div>

      {/* Orders Grid */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-gray-400">{t('aOrdsLoading')}</p>
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-xl border border-gray-700">
          <ImageIcon className="w-16 h-16 text-gray-600 mx-auto mb-4" />
          <p className="text-gray-400">{t('aOrdsEmpty')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
          {orders.map((order) => (
            <div key={order.id} className="bg-gray-800 rounded-lg shadow-sm overflow-hidden border border-gray-700 hover:border-brand-500 transition-colors">
              <div className="relative h-32 bg-gray-700">
                {order.product_image ? (
                  <img
                    src={API_CONFIG.getImageUrl(order.product_image)}
                    alt={order.product_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <ImageIcon className="w-16 h-16 text-gray-600" />
                  </div>
                )}
                <div className="absolute top-2 right-2 flex gap-1">
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    order.status === 'active' ? 'bg-green-500 text-white' : 'bg-red-500 text-white'
                  }`}>
                    {order.status === 'active' ? t('aOrdsStatusActive') : t('aOrdsStatusInactive')}
                  </span>
                </div>
              </div>
              <div className="p-3">
                <h3 className="font-semibold text-white text-sm mb-2 truncate">{order.product_name}</h3>
                <div className="space-y-1 mb-3">
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">{t('aOrdsPrice')}</span>
                    <span className="font-semibold text-white">{formatCurrency(order.price)}</span>
                  </div>
                  <div className="flex justify-between text-xs">
                    <span className="text-gray-400">{t('aOrdsCommission')}</span>
                    <span className="font-semibold text-green-400">{formatCurrency(order.commission)}</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEdit(order)}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-gray-700 text-white rounded hover:bg-gray-600 text-xs"
                  >
                    <Edit className="w-3 h-3" />
                    {t('aOrdsEdit')}
                  </button>
                  <button
                    onClick={() => handleDelete(order.id)}
                    className="flex-1 flex items-center justify-center gap-1 px-2 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 text-xs"
                  >
                    <Trash2 className="w-3 h-3" />
                    {t('aOrdsDelete')}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto border border-gray-700">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-white">
                  {editingOrder ? t('aOrdsEditOrder') : t('aOrdsAddNew')}
                </h2>
                <button onClick={() => { setShowModal(false); resetForm(); }} className="text-gray-400 hover:text-white">
                  <X className="w-6 h-6" />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('aOrdsProductName')}</label>
                  <input
                    type="text"
                    required
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                    placeholder={t('aOrdsProductNamePlaceholder')}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('aOrdsProductImage')}</label>
                  <div className="flex items-center gap-4">
                    <label className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-gray-700 border-2 border-dashed border-gray-600 text-gray-300 rounded-lg hover:border-brand-500 cursor-pointer transition-colors">
                      <Upload className="w-5 h-5" />
                      <span>{imageFile ? imageFile.name : t('aOrdsChooseImage')}</span>
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleImageChange}
                        className="hidden"
                      />
                    </label>
                  </div>
                  {imagePreview && (
                    <img src={imagePreview} alt="Preview" className="mt-3 w-full h-48 object-cover rounded-lg" />
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">{t('aOrdsPriceUsdt')}</label>
                  <input
                    type="number"
                    required
                    min="1"
                    value={formData.price}
                    onChange={(e) => setFormData({ ...formData, price: e.target.value })}
                    className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                    placeholder="150000"
                  />
                  <p className="mt-2 text-xs text-gray-400">{t('aOrdsCommissionHint')}</p>
                </div>

                <div>
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">{t('aOrdsStatus')}</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                      className="w-full px-4 py-2 bg-gray-700 border border-gray-600 text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="active">{t('aOrdsStatusActive')}</option>
                      <option value="inactive">{t('aOrdsStatusInactive')}</option>
                    </select>
                  </div>
                </div>

                <div className="flex gap-3 pt-4">
                  <button
                    type="button"
                    onClick={() => { setShowModal(false); resetForm(); }}
                    className="flex-1 px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600"
                  >
                    {t('aOrdsCancel')}
                  </button>
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex-1 px-4 py-2 bg-brand-500 text-white rounded-lg hover:bg-brand-500 font-medium disabled:opacity-50"
                  >
                    {submitting ? t('aOrdsProcessing') : (editingOrder ? t('aOrdsUpdate') : t('aOrdsAddOrder'))}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
